import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

// PrimeNG Modules
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { DialogModule } from 'primeng/dialog';
// Removed 'of' import since we're not using autocomplete filtering anymore

// Angular Material
import { AngularMaterialModule } from '../../../angular-material.module';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

// Services
import { MembersService } from '../../../services/members.service';
import { snackBarService } from '../../../services/snack-bar.service';
import { AssignService } from '../../assign/assign.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-add-members',
  standalone: true,
  imports: [
    CommonModule,
    AngularMaterialModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    RadioButtonModule,
    ButtonModule,
    DropdownModule,
    DialogModule,
  ],
  templateUrl: './add-members.component.html',
  styleUrl: './add-members.component.scss',
})
export class AddMembersComponent implements OnInit {
  addMemberForm: FormGroup;
  displayDialog: boolean = true;
  isEditMode = false;
  account_id: any;

  // Dropdown options
  projectOptions: any[] = [];
  wingOptions: any[] = [];
  floorOptions: any[] = [];
  unitOptions: any[] = [];
  roleOptions: any[] = [];

  // Removed filtered options since we're using mat-select instead of autocomplete

  // Selected values for cascade filtering
  selectedProject: string = '';
  selectedWing: string = '';
  selectedFloor: string = '';
  selectedFloorUnit: string = '';

  // Profile picture handling
  selectedProfilePic: File | null = null;
  profilePicPreview: string | null = null;
  
  // Flag to prevent clearing form fields during patching
  isPatchingData: boolean = false;

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private membersService: MembersService,
    private snackBarService: snackBarService,
    private assignService: AssignService,
    private dialogRef: MatDialogRef<AddMembersComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.addMemberForm = this.fb.group({
      account_id: ['', Validators.required],
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      block_id: ['', Validators.required],
      floor_id: ['', Validators.required],
      floor_unit_id: ['', Validators.required],
      role_id: ['', Validators.required],
      city: [''],
      state: [''],
      username: ['', Validators.required],
      email_id: ['', Validators.email],
      is_owner: ['Owner'],
      mob_no: [
        '',
        [Validators.required, Validators.pattern(/^[6-9][0-9]{9}$/)],
      ],
      created_by: new FormControl(sessionStorage.getItem('session_id'), [
        Validators.required,
      ]),
      updated_by: new FormControl(''),
      created_at: new FormControl(''),
      updated_at: new FormControl(''),
    });
  }

  ngOnInit() {
    this.account_id = sessionStorage.getItem('account_id');
    this.loadProjects(); // Load projects first
    this.fetchRoleDropdown(); // Load roles (independent)

    // If dialog opened in edit mode
    if (this.data?.mode === 'edit' && this.data?.user) {
      this.isEditMode = true;
      this.patchFormData(this.data.user);
    }

    // No need for value change subscriptions since we're using mat-select
  }

  /** ✅ Prefill form when editing */
  private patchFormData(user: any) {
    console.log('Patching form data for user:', user); // Debug log
    this.isPatchingData = true; // Set flag to prevent clearing form fields
    
    // First patch basic form data that doesn't depend on dropdowns
    this.addMemberForm.patchValue({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      city: user.city || '',
      state: user.state || '',
      username: user.username || '',
      email_id: user.email_id || '',
      is_owner: user.is_owner === 1 ? 'Owner' : 'Renter',
      mob_no: user.mob_no || '',
      role_id: user.role_id || '',
      updated_by: sessionStorage.getItem('session_id'),
      created_at: user.created_at,
      updated_at: user.updated_at,
    });

    // Set profile image preview if exists
    if (user.profile_pic && user.profile_pic.trim() !== '') {
      this.profilePicPreview = `${environment.storageUrl}/${user.profile_pic}`;
      console.log('Profile image URL:', this.profilePicPreview);
      this.cdr.detectChanges(); // Trigger change detection
    } else {
      console.log('No profile image found:', user.profile_pic);
    }
    
    // Set project first
    this.selectedProject = user.account_id?.toString() || '';
    
    // Load wings first
    this.loadWingsFromProject(user.account_id).then(() => {
      // Set wing and load floors
      this.selectedWing = user.block_id?.toString() || '';
      this.loadFloorsFromWing(user.block_id).then(() => {
        // Set floor and load units
        this.selectedFloor = user.floor_id?.toString() || '';
        this.loadUnitsFromFloor(user.block_id, user.floor_id).then(() => {
          // Set unit
          this.selectedFloorUnit = user.floor_unit_id?.toString() || '';
          
          // Now patch the dropdown values after all options are loaded
          setTimeout(() => {
            console.log('Patching form values:', {
              account_id: user.account_id,
              block_id: user.block_id,
              floor_id: user.floor_id,
              floor_unit_id: user.floor_unit_id,
              available_units: this.unitOptions,
              unit_values: this.unitOptions.map(u => u.floor_unit_id)
            });
            
            // Check if the floor_unit_id exists in the available units
            const unitExists = this.unitOptions.some(unit => unit.floor_unit_id == user.floor_unit_id);
            console.log('Unit exists in options:', unitExists);
            
            // Use setValue for more reliable form control updates
            this.addMemberForm.get('account_id')?.setValue(user.account_id || '');
            this.addMemberForm.get('block_id')?.setValue(user.block_id || '');
            this.addMemberForm.get('floor_id')?.setValue(user.floor_id || '');
            
            // Only set floor_unit_id if it exists in the options
            if (unitExists) {
              this.addMemberForm.get('floor_unit_id')?.setValue(user.floor_unit_id || '');
            } else {
              console.log('Floor unit ID not found in options, skipping setValue');
            }
            
            console.log('Form values after patching:', {
              account_id: this.addMemberForm.get('account_id')?.value,
              block_id: this.addMemberForm.get('block_id')?.value,
              floor_id: this.addMemberForm.get('floor_id')?.value,
              floor_unit_id: this.addMemberForm.get('floor_unit_id')?.value,
            });
            
            this.cdr.detectChanges(); // Trigger change detection for form updates
            this.isPatchingData = false; // Reset flag after patching is complete
          }, 200); // Increased delay to ensure dropdowns are rendered
          
        }).catch(() => {
          // Handle error loading units
          this.isPatchingData = false;
          this.snackBar.open('Failed to load unit data', 'Close', { duration: 3000 });
        });
      }).catch(() => {
        // Handle error loading floors
        this.isPatchingData = false;
        this.snackBar.open('Failed to load floor data', 'Close', { duration: 3000 });
      });
    }).catch(() => {
      // Handle error loading wings
      this.isPatchingData = false;
      this.snackBar.open('Failed to load wing data', 'Close', { duration: 3000 });
    });
  }

  /** ✅ Load projects */
  loadProjects(): void {
    this.assignService.fetchProjects({}).subscribe({
      next: res => {
        this.projectOptions = res || [];
      },
      error: () => this.snackBar.open('Failed to load projects', 'Close', { duration: 3000 })
    });
  }

  /** ✅ Load roles (independent) */
  fetchRoleDropdown(): void {
    this.membersService.roleDropdown().subscribe({
      next: (res: any) => {
        this.roleOptions = res;
      },
      error: () =>
        this.snackBar.open('Failed to load Role options', 'Close', {
          duration: 3000,
        }),
    });
  }

  /** ✅ Cascade change handlers */
  onProjectChange(event: any): void {
    this.selectedProject = event.value;
    this.selectedWing = '';
    this.selectedFloor = '';
    this.selectedFloorUnit = '';
    this.wingOptions = [];
    this.floorOptions = [];
    this.unitOptions = [];
    
    // Clear dependent form fields only if not patching data
    if (!this.isPatchingData) {
      this.addMemberForm.patchValue({
        block_id: '',
        floor_id: '',
        floor_unit_id: ''
      });
    }
    
    if (this.selectedProject) {
      this.loadWingsFromProject(this.selectedProject);
    } else {
      this.clearWingDropdown();
    }
  }

  onWingChange(event: any): void {
    this.selectedWing = event.value;
    this.selectedFloor = '';
    this.selectedFloorUnit = '';
    this.floorOptions = [];
    this.unitOptions = [];
    
    // Clear dependent form fields only if not patching data
    if (!this.isPatchingData) {
      this.addMemberForm.patchValue({
        floor_id: '',
        floor_unit_id: ''
      });
    }
    
    if (this.selectedWing) {
      this.loadFloorsFromWing(this.selectedWing);
    } else {
      this.clearFloorDropdown();
    }
  }

  onFloorChange(event: any): void {
    this.selectedFloor = event.value;
    this.selectedFloorUnit = '';
    this.unitOptions = [];
    
    // Clear dependent form fields only if not patching data
    if (!this.isPatchingData) {
      this.addMemberForm.patchValue({
        floor_unit_id: ''
      });
    }
    
    if (this.selectedFloor) {
      this.loadUnitsFromFloor(this.selectedWing, this.selectedFloor);
    } else {
      this.clearUnitDropdown();
    }
  }

  onFloorUnitChange(event: any): void {
    this.selectedFloorUnit = event.value;
  }

  /** ✅ Load dependent dropdowns */
  private clearWingDropdown(): void {
    this.wingOptions = [];
    this.selectedWing = '';
  }

  private loadWingsFromProject(accountId: string | number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.membersService.blockDropdown({ account_id: accountId }).subscribe(
        (res: any) => {
          this.wingOptions = Array.isArray(res) ? res : [];
          resolve();
        },
        () => {
          this.wingOptions = [];
          this.snackBar.open('Failed to load wings', 'Close', { duration: 3000 });
          reject();
        }
      );
    });
  }

  private clearFloorDropdown(): void {
    this.floorOptions = [];
    this.selectedFloor = '';
  }

  private loadFloorsFromWing(blockId: string | number): Promise<void> {
    return new Promise((resolve, reject) => {
      const accountId = this.selectedProject;
      this.membersService.floorDropdown({ account_id: accountId, block_id: blockId }).subscribe(
        (res: any) => {
          this.floorOptions = Array.isArray(res) ? res : [];
          resolve();
        },
        () => {
          this.floorOptions = [];
          this.snackBar.open('Failed to load floors', 'Close', { duration: 3000 });
          reject();
        }
      );
    });
  }

  private clearUnitDropdown(): void {
    this.unitOptions = [];
    this.selectedFloorUnit = '';
  }

  private loadUnitsFromFloor(blockId: string | number, floorId: string | number): Promise<void> {
    return new Promise((resolve, reject) => {
      const accountId = this.selectedProject;
      console.log('Loading units with params:', { account_id: accountId, block_id: blockId, floor_id: floorId });
      this.membersService.unitDropdown({ account_id: accountId, block_id: blockId, floor_id: floorId }).subscribe(
        (res: any) => {
          console.log('Units API response:', res);
          this.unitOptions = Array.isArray(res) ? res : [];
          console.log('Units loaded:', this.unitOptions);
          resolve();
        },
        (error) => {
          console.error('Error loading units:', error);
          this.unitOptions = [];
          this.snackBar.open('Failed to load units', 'Close', { duration: 3000 });
          reject();
        }
      );
    });
  }

  /** ✅ Handle profile picture selection */
  onProfilePicSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedProfilePic = file;
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.profilePicPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  /** ✅ Remove profile picture */
  removeProfilePic(): void {
    this.selectedProfilePic = null;
    this.profilePicPreview = null;
  }

  /** ✅ Handle image error */
  handleImageError(event: any): void {
    console.log('Image failed to load, setting fallback');
    // Set fallback image when the profile image fails to load
    event.target.src = 'assets/Images/pngtree-user-profile-avatar-png-image_13369989.png';
  }

  /** ✅ Convert file to binary format */
  private convertFileToBinary(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        // Convert ArrayBuffer to binary string
        const binaryString = Array.from(new Uint8Array(arrayBuffer))
          .map(byte => String.fromCharCode(byte))
          .join('');
        resolve(binaryString);
      };
      reader.onerror = error => reject(error);
    });
  }

  /** ✅ Add or Update Member */
  async onSubmit(formDirective: FormGroupDirective) {
    if (this.addMemberForm.invalid) return;

    this.snackBar.open('Processing...', undefined, { duration: 2000 });

    if (this.isEditMode) {
      // EDIT - Send FormData per API contract
      try {
        const formData = new FormData();

        formData.append('user_id', String(this.data.user.user_id));
        formData.append('account_id', this.addMemberForm.value.account_id);
        formData.append('role_id', this.addMemberForm.value.role_id);
        formData.append('first_name', this.addMemberForm.value.first_name);
        formData.append('last_name', this.addMemberForm.value.last_name);
        formData.append('email_id', this.addMemberForm.value.email_id ?? '');
        formData.append('username', this.addMemberForm.value.username);
        formData.append('mob_no', this.addMemberForm.value.mob_no);
        formData.append('is_owner', this.addMemberForm.value.is_owner === 'Owner' ? '1' : '0');
        formData.append('block_id', this.addMemberForm.value.block_id);
        formData.append('floor_id', this.addMemberForm.value.floor_id);
        formData.append('floor_unit_id', this.addMemberForm.value.floor_unit_id);
        formData.append('state', this.addMemberForm.value.state ?? '');
        formData.append('city', this.addMemberForm.value.city ?? '');
        formData.append('updated_by', sessionStorage.getItem('session_id') || '');

        // Optional: profile picture if provided
        if (this.selectedProfilePic) {
          const binaryData = await this.convertFileToBinary(this.selectedProfilePic);
          formData.append('profile_pic', JSON.stringify([binaryData, this.selectedProfilePic.name]));
        }

        this.membersService.editUser(formData).subscribe({
          next: (res: any) => {
            if (res?.success) {
              this.snackBar.open(res.message || 'User updated successfully', 'Close', { duration: 3000 });
              this.dialogRef.close(true);
            } else {
              this.snackBar.open(res?.message || 'Error while updating member', 'Close', { duration: 3000 });
            }
          },
          error: (err: any) => {
            console.log(err);
            this.snackBar.open('Error while updating member', 'Close', { duration: 3000 });
          }
        });
      } catch (error) {
        console.error('Error preparing edit payload:', error);
        this.snackBar.open('Error processing profile picture', 'Close', { duration: 3000 });
      }
    } else {
      // ADD - New FormData implementation
      try {
        const formData = new FormData();
        
        // Add all form fields
        formData.append('account_id', this.addMemberForm.value.account_id);
        formData.append('role_id', this.addMemberForm.value.role_id);
        formData.append('first_name', this.addMemberForm.value.first_name);
        formData.append('last_name', this.addMemberForm.value.last_name);
        formData.append('email_id', this.addMemberForm.value.email_id);
        formData.append('username', this.addMemberForm.value.username);
        formData.append('mob_no', this.addMemberForm.value.mob_no);
        formData.append('is_owner', this.addMemberForm.value.is_owner === 'Owner' ? '1' : '0');
        formData.append('block_id', this.addMemberForm.value.block_id);
        formData.append('floor_id', this.addMemberForm.value.floor_id);
        formData.append('floor_unit_id', this.addMemberForm.value.floor_unit_id);
        formData.append('state', this.addMemberForm.value.state);
        formData.append('city', this.addMemberForm.value.city);
        formData.append('created_by', sessionStorage.getItem('session_id') || '');

        // Add profile picture if selected
        if (this.selectedProfilePic) {
          const binaryData = await this.convertFileToBinary(this.selectedProfilePic);
          formData.append('profile_pic', JSON.stringify([binaryData, this.selectedProfilePic.name]));
        }

        this.membersService.addMembers(formData).subscribe({
          next: (res: any) => {
            if (res.success) {
              formDirective.resetForm();
              this.addMemberForm.reset();
              this.selectedProfilePic = null;
              this.profilePicPreview = null;
              this.snackBar.open(res.message || 'Member Added Successfully', 'Close', {
                duration: 3000,
              });
              this.dialogRef.close(true);
            } else {
              this.snackBar.open('Error while adding member', 'Close', {
                duration: 3000,
              });
            }
          },
          error: (err: any) => {
            console.log(err);
            this.snackBar.open('Error while adding member', 'Close', {
              duration: 3000,
            });
          },
        });
      } catch (error) {
        console.error('Error converting file to binary:', error);
        this.snackBar.open('Error processing profile picture', 'Close', {
          duration: 3000,
        });
      }
    }
  }

  // Display functions removed since we're using mat-select instead of autocomplete

  closeDialog() {
    this.dialogRef.close(false);
  }

  // Debug method to manually test form patching
  debugFormValues() {
    console.log('Current form values:', {
      account_id: this.addMemberForm.get('account_id')?.value,
      block_id: this.addMemberForm.get('block_id')?.value,
      floor_id: this.addMemberForm.get('floor_id')?.value,
      floor_unit_id: this.addMemberForm.get('floor_unit_id')?.value,
    });
    console.log('Current dropdown options:', {
      projects: this.projectOptions.length,
      wings: this.wingOptions.length,
      floors: this.floorOptions.length,
      units: this.unitOptions.length,
    });
    console.log('Selected values:', {
      selectedProject: this.selectedProject,
      selectedWing: this.selectedWing,
      selectedFloor: this.selectedFloor,
      selectedFloorUnit: this.selectedFloorUnit,
    });
  }
}
